import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CourseCard from '../components/CourseCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import ErrorMessage from '../components/ui/ErrorMessage';
import courseService from '../services/courseService';

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch courses from API on mount
  useEffect(() => {
    fetchCourses();
  }, []);

  /**
   * Fetch all courses from the backend
   */
  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await courseService.getAllCourses();
      setCourses(data);
      setFilteredCourses(data);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
      setError(err.response?.data?.detail || 'Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Filter courses when search term changes
  useEffect(() => {
    if (!courses.length) return;

    let filtered = courses;

    // Filter by search term
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(course =>
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredCourses(filtered);
  }, [searchTerm, courses]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
        <div className="container mx-auto max-w-7xl px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Courses</h1>
          <p className="text-xl text-blue-100">
            Explore our comprehensive range of banking promotion courses
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="bg-white py-8 shadow-md">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="flex flex-col items-center justify-center gap-4">
            {/* Search Bar */}
            <div className="w-full max-w-4xl">
              <div className="relative rounded-full">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && e.target.blur()}
                  placeholder="Search courses..."
                  className="py-6 lg:py-8 pl-8 pr-20 text-lg w-full text-black rounded-full focus:outline-none shadow-lg"
                  autoComplete="off"
                />
                <button
                  className="bg-blue-600 p-5 rounded-full absolute right-2 top-2 hover:bg-blue-700 transition-colors"
                  onClick={() => {}}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img" className="text-white text-4xl inline-block" width="1em" height="1em" viewBox="0 0 24 24">
                    <g fill="none" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="11.5" cy="11.5" r="9.5"></circle>
                      <path strokeLinecap="round" d="M18.5 18.5L22 22"></path>
                    </g>
                  </svg>
                </button>
              </div>
            </div>

            {/* Results Count */}
            {searchTerm && (
              <div className="text-slate-600">
                <p className="text-base font-medium">
                  Found {filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-7xl">
          {/* Loading State */}
          {loading && <LoadingSpinner message="Loading courses..." />}

          {/* Error State */}
          {error && !loading && <ErrorMessage message={error} onRetry={fetchCourses} />}

          {/* Courses Grid */}
          {!loading && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {filteredCourses.length > 0 ? (
                filteredCourses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <p className="text-slate-600 text-lg">
                    {searchTerm
                      ? 'No courses match your search criteria. Try adjusting your search.'
                      : 'No courses available yet. Please check back later!'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Courses;
